// /admin/ads → "בדיקות A/B": build a fair test — the chosen videos × the chosen flows, one ad
// set per combination with the same audience and budget — then read the result as a grid:
// which video and which flow bring signups (and paying customers) cheapest.
import {FC, useEffect, useState} from 'react'
import MultiResult from './MultiResult'
import {
  AdTest,
  Creative,
  createTest,
  getCreatives,
  getTests,
  Launch,
  setTestStatus,
} from './_requests'

const ils = (n: number | null | undefined) =>
  n == null || !isFinite(n) ? '—' : `₪${Math.round(n).toLocaleString('he-IL')}`
const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'

const FLOWS: {key: 'website' | 'lead_form' | 'whatsapp'; label: string; note: string}[] = [
  {key: 'website', label: '🌐 דף הרשמה', note: 'גוגל / פייסבוק / טלפון באתר'},
  {key: 'lead_form', label: '📝 טופס פייסבוק', note: 'נשאר בפייסבוק, חשבון נפתח אוטומטית'},
  {key: 'whatsapp', label: '💬 וואטסאפ', note: 'הבוט פותח חשבון בצ׳אט (דורש קישור הדף לוואטסאפ)'},
]

type Cell = {
  spend: number
  clicks: number
  leads: number
  signups: number
  paid: number
  revenue: number
  status?: string
  error?: string
}
const zero = (): Cell => ({spend: 0, clicks: 0, leads: 0, signups: 0, paid: 0, revenue: 0})
const add = (a: Cell, b: Cell) => {
  a.spend += b.spend
  a.clicks += b.clicks
  a.leads += b.leads
  a.signups += b.signups
  a.paid += b.paid
  a.revenue += b.revenue
}
// Signups when there are any, else leads — the earliest signal that's comparable across flows.
const costPer = (c: Cell) => (c.signups ? c.spend / c.signups : c.leads ? c.spend / c.leads : null)

const AbTests: FC<{
  selected: string[]
  setSelected: (k: string[]) => void
  launches: Launch[]
  lookalikes: string[]
  onCreated: () => void
  goCreatives: () => void
}> = ({selected, setSelected, launches, lookalikes, onCreated, goCreatives}) => {
  const [lib, setLib] = useState<Creative[]>([])
  const [tests, setTests] = useState<AdTest[]>([])
  const [flows, setFlows] = useState<string[]>(['website', 'lead_form'])
  const [audience, setAudience] = useState('broad')
  const [budget, setBudget] = useState(50)
  const [name, setName] = useState('')
  const [mode, setMode] = useState<'cells' | 'one_ad'>('cells')
  const [busy, setBusy] = useState('')
  const [msg, setMsg] = useState<{ok: boolean; text: string} | null>(null)

  const loadTests = () => getTests().then(({data}) => setTests(data.tests))
  useEffect(() => {
    getCreatives(false)
      .then(({data}) => setLib(data.creatives))
      .catch(() => {})
    loadTests().catch(() => {})
  }, [])

  const byKey: Record<string, Creative> = {}
  lib.forEach((c) => (byKey[c.key] = c))
  // Ads that will be created: one per video×flow, or one multi-video ad per flow.
  const cells = mode === 'one_ad' ? flows.length : selected.length * flows.length
  const oneAdBlocked = mode === 'one_ad' && (flows.includes('whatsapp') || selected.length < 2)

  const create = async () => {
    setBusy('create')
    setMsg(null)
    try {
      await createTest({
        creative_keys: selected,
        destinations: flows,
        mode,
        audience,
        daily_budget: budget,
        name,
      })
      setMsg({
        ok: true,
        text: `הבדיקה נוצרה: ${cells} מודעות נבנות עכשיו ברקע (מושהות). כשכולן מוכנות — "הפעל את כל הבדיקה".`,
      })
      setSelected([])
      setName('')
      await loadTests()
      onCreated()
    } catch (e) {
      setMsg({ok: false, text: errMsg(e)})
    } finally {
      setBusy('')
    }
  }

  const switchTest = async (t: AdTest, status: 'ACTIVE' | 'PAUSED') => {
    if (
      status === 'ACTIVE' &&
      !window.confirm(
        `להפעיל ${t.cells.length} מודעות × ₪${t.daily_budget} = ₪${
          t.cells.length * t.daily_budget
        } ליום?`
      )
    )
      return
    setBusy(t._id)
    setMsg(null)
    try {
      const {data} = await setTestStatus(t._id, status)
      if (data.errors && data.errors.length) setMsg({ok: false, text: data.errors.join(' · ')})
      await loadTests()
      onCreated()
    } catch (e) {
      setMsg({ok: false, text: errMsg(e)})
    } finally {
      setBusy('')
    }
  }

  return (
    <div>
      <div className='card card-bordered p-5 mb-6'>
        <h4 className='fw-bold mb-1'>בדיקה חדשה</h4>
        <div className='text-muted fs-7 mb-4'>
          כל סרטון × כל דרך = מודעה נפרדת, עם אותו קהל ואותו תקציב — כך ההשוואה הוגנת. מומלץ 2–3
          סרטונים × 2 דרכים, 5–7 ימים.
        </div>

        <div className='fw-bold fs-7 mb-2'>1. סרטונים ({selected.length})</div>
        {selected.length === 0 ? (
          <div className='mb-4'>
            <button className='btn btn-sm btn-light-primary' onClick={goCreatives}>
              בחירת סרטונים בלשונית קריאייטיבים
            </button>
          </div>
        ) : (
          <div className='d-flex gap-3 flex-wrap mb-4'>
            {selected.map((k) => (
              <div
                key={k}
                className='d-flex align-items-center gap-2 border rounded p-2'
                style={{maxWidth: 260}}
              >
                <div
                  style={{
                    width: 36,
                    height: 64,
                    borderRadius: 6,
                    background: `#000 url("${byKey[k]?.thumb || ''}") center/cover`,
                  }}
                />
                <div className='fs-8'>
                  <div className='fw-bold text-truncate' style={{maxWidth: 160}}>
                    {byKey[k]?.title || k}
                  </div>
                  <div className='text-muted'>
                    {byKey[k]?.stats.cpl != null
                      ? `${ils(byKey[k].stats.cpl)} לליד בעבר`
                      : 'בלי היסטוריה'}
                  </div>
                </div>
                <button
                  className='btn btn-sm btn-icon btn-light'
                  onClick={() => setSelected(selected.filter((x) => x !== k))}
                >
                  ✕
                </button>
              </div>
            ))}
            <button className='btn btn-sm btn-light' onClick={goCreatives}>
              + עוד
            </button>
          </div>
        )}

        <div className='fw-bold fs-7 mb-2'>איך לבדוק את הסרטונים</div>
        <div className='d-flex gap-3 flex-wrap mb-4'>
          {(
            [
              [
                'cells',
                'מודעה נפרדת לכל סרטון',
                'תקציב שווה לכל סרטון — השוואה הוגנת ומדויקת. עולה יותר (התקציב מתחלק).',
              ],
              [
                'one_ad',
                'מודעה אחת עם כל הסרטונים',
                'פייסבוק מריץ את כולם ומעביר תקציב למנצח לבד. זול ומהיר, אבל פייסבוק מחליט כמה כל סרטון מקבל. (דף הרשמה / טופס בלבד)',
              ],
            ] as const
          ).map(([k, label, note]) => (
            <label
              key={k}
              className='form-check border rounded p-3 ps-10 m-0'
              style={{flex: '1 1 260px', background: mode === k ? '#eef4ff' : undefined}}
            >
              <input
                className='form-check-input'
                type='radio'
                name='test-mode'
                checked={mode === k}
                onChange={() => setMode(k)}
              />
              <span className='form-check-label'>
                <b className='fs-7'>{label}</b>
                <div className='text-muted fs-8'>{note}</div>
              </span>
            </label>
          ))}
        </div>

        <div className='fw-bold fs-7 mb-2'>2. דרכים להשוות</div>
        <div className='d-flex gap-3 flex-wrap mb-4'>
          {FLOWS.map((f) => (
            <label
              key={f.key}
              className='form-check border rounded p-3 ps-10 m-0'
              style={{minWidth: 220}}
            >
              <input
                className='form-check-input'
                type='checkbox'
                checked={flows.includes(f.key)}
                onChange={(e) =>
                  setFlows(e.target.checked ? [...flows, f.key] : flows.filter((x) => x !== f.key))
                }
              />
              <span className='form-check-label'>
                <b className='fs-7'>{f.label}</b>
                <div className='text-muted fs-8'>{f.note}</div>
              </span>
            </label>
          ))}
        </div>

        <div className='row g-3 mb-4'>
          <div className='col-md-4'>
            <label className='fs-8 text-muted'>קהל (זהה לכל המודעות)</label>
            <select
              className='form-select form-select-sm'
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
            >
              <option value='broad'>רחב — Advantage+</option>
              {lookalikes.includes('lal_1') && <option value='lal_1'>Lookalike 1%</option>}
              {lookalikes.includes('lal_3') && <option value='lal_3'>Lookalike 3%</option>}
            </select>
          </div>
          <div className='col-md-3'>
            <label className='fs-8 text-muted'>תקציב יומי לכל מודעה ₪</label>
            <input
              type='number'
              min={20}
              className='form-control form-control-sm'
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
            />
          </div>
          <div className='col-md-5'>
            <label className='fs-8 text-muted'>שם לבדיקה (לא חובה)</label>
            <input
              className='form-control form-control-sm'
              value={name}
              placeholder='למשל: רילס חדשים מול הישנים'
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        <div className='d-flex align-items-center gap-3 flex-wrap'>
          <button
            className='btn btn-primary'
            disabled={busy === 'create' || !cells || cells > 12 || oneAdBlocked}
            onClick={create}
          >
            {busy === 'create' ? 'יוצר…' : `צור בדיקה: ${cells} מודעות (מושהות)`}
          </button>
          <span className='fs-7'>
            סה״כ כשפעילה: <b>₪{(cells * budget).toLocaleString('he-IL')} ליום</b>
            {cells > 12 && <span className='text-danger'> · עד 12 שילובים בבדיקה</span>}
            {oneAdBlocked && (
              <span className='text-danger'> · מודעה אחת: לפחות 2 סרטונים, ובלי וואטסאפ</span>
            )}
          </span>
        </div>
        {msg && (
          <div className={`alert mt-3 mb-0 py-2 fs-7 ${msg.ok ? 'alert-success' : 'alert-danger'}`}>
            {msg.text}
          </div>
        )}
      </div>

      <h4 className='fw-bold mb-3'>בדיקות</h4>
      {tests.length === 0 && <div className='text-muted'>עוד אין בדיקות.</div>}
      {tests.map((t) => {
        const mine = launches.filter((l) => (l as any).test === t._id)
        const grid: Record<string, Record<string, Cell>> = {}
        const rowTot: Record<string, Cell> = {}
        const colTot: Record<string, Cell> = {}
        const total = zero()
        t.creative_keys.forEach((k) => {
          grid[k] = {}
          rowTot[k] = zero()
        })
        t.destinations.forEach((d) => (colTot[d] = zero()))
        mine.forEach((l) => {
          const k = (l as any).creative_key as string
          const d = l.destination || 'website'
          if (!grid[k]) return
          const c: Cell = {
            spend: l.meta?.spend || 0,
            clicks: l.meta?.clicks || 0,
            leads: (l.funnel.form_leads || 0) + (l.funnel.wa_conversations || 0),
            signups: l.funnel.signups,
            paid: l.funnel.paid,
            revenue: l.funnel.revenue,
            status:
              l.status === 'creating'
                ? 'נבנית…'
                : l.status === 'failed'
                ? 'נכשלה'
                : l.meta?.effective_status,
            error: l.error,
          }
          grid[k][d] = c
          add(rowTot[k], c)
          add(colTot[d], c)
          add(total, c)
        })
        const bestOf = (m: Record<string, Cell>) =>
          Object.keys(m)
            .filter((k) => costPer(m[k]) != null && (m[k].signups >= 3 || m[k].leads >= 5))
            .sort((a, b) => costPer(m[a])! - costPer(m[b])!)[0]
        const bestCreative = bestOf(rowTot)
        const bestFlow = bestOf(colTot)
        const building = mine.filter((l) => l.status === 'creating').length
        const failed = mine.filter((l) => l.status === 'failed').length
        return (
          <div key={t._id} className='card card-bordered p-5 mb-5'>
            <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3'>
              <div>
                <b className='fs-5'>בדיקה {t.name}</b>
                <div className='text-muted fs-8'>
                  {new Date(t.createdAt).toLocaleDateString('he-IL')} · {t.cells.length} מודעות × ₪
                  {t.daily_budget}/יום · קהל: {t.audience === 'broad' ? 'רחב' : t.audience}
                  {building ? ` · ${building} עוד נבנות` : ''}
                  {failed ? ` · ${failed} נכשלו` : ''}
                </div>
              </div>
              <div className='d-flex gap-2 align-items-center'>
                <span
                  className={`badge badge-light-${t.status === 'ACTIVE' ? 'success' : 'secondary'}`}
                >
                  {t.status === 'ACTIVE' ? 'פעילה' : 'מושהית'}
                </span>
                {t.status === 'ACTIVE' ? (
                  <button
                    className='btn btn-sm btn-light-warning'
                    disabled={busy === t._id}
                    onClick={() => switchTest(t, 'PAUSED')}
                  >
                    השהה את כל הבדיקה
                  </button>
                ) : (
                  <button
                    className='btn btn-sm btn-success'
                    disabled={busy === t._id || building > 0}
                    onClick={() => switchTest(t, 'ACTIVE')}
                  >
                    הפעל את כל הבדיקה
                  </button>
                )}
              </div>
            </div>

            {(bestCreative || bestFlow) && (
              <div className='alert alert-success py-2 fs-7'>
                {bestCreative && (
                  <>
                    🏆 הסרטון הכי משתלם: <b>{byKey[bestCreative]?.title || bestCreative}</b> (
                    {ils(costPer(rowTot[bestCreative]))})
                  </>
                )}
                {bestCreative && bestFlow && ' · '}
                {bestFlow && (
                  <>
                    🏆 הדרך הכי משתלמת: <b>{FLOWS.find((f) => f.key === bestFlow)?.label}</b> (
                    {ils(costPer(colTot[bestFlow]))})
                  </>
                )}
              </div>
            )}

            {t.mode === 'one_ad' ? (
              <MultiResult test={t} byKey={byKey} launches={mine} />
            ) : (
              <div className='table-responsive'>
                <table className='table table-bordered align-middle fs-8 mb-1'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>סרטון \ דרך</th>
                      {t.destinations.map((d) => (
                        <th key={d}>{FLOWS.find((f) => f.key === d)?.label}</th>
                      ))}
                      <th>סה״כ לסרטון</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.creative_keys.map((k) => (
                      <tr key={k} style={bestCreative === k ? {background: '#ecfdf3'} : undefined}>
                        <td style={{maxWidth: 200}}>
                          <div className='d-flex align-items-center gap-2'>
                            <div
                              style={{
                                width: 27,
                                height: 48,
                                borderRadius: 4,
                                flex: '0 0 auto',
                                background: `#000 url("${byKey[k]?.thumb || ''}") center/cover`,
                              }}
                            />
                            <span className='fw-bold text-truncate'>{byKey[k]?.title || k}</span>
                          </div>
                        </td>
                        {t.destinations.map((d) => {
                          const c = grid[k][d]
                          return (
                            <td
                              key={d}
                              style={bestFlow === d ? {background: '#f0fdf4'} : undefined}
                            >
                              {!c ? (
                                '—'
                              ) : (
                                <>
                                  <div className='fw-bold'>{ils(costPer(c))}</div>
                                  <div className='text-muted'>
                                    {ils(c.spend)} · {c.signups} הרשמות
                                    {c.leads ? ` · ${c.leads} לידים` : ''} · {c.paid} שילמו
                                  </div>
                                  {c.status && (
                                    <div className={c.error ? 'text-danger' : 'text-muted'}>
                                      {c.error || c.status}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          )
                        })}
                        <td className='fw-bold'>
                          {ils(costPer(rowTot[k]))}
                          <div className='text-muted fw-normal'>
                            {rowTot[k].signups} הרשמות · {rowTot[k].paid} שילמו
                          </div>
                        </td>
                      </tr>
                    ))}
                    <tr className='fw-bold'>
                      <td>סה״כ לדרך</td>
                      {t.destinations.map((d) => (
                        <td key={d}>
                          {ils(costPer(colTot[d]))}
                          <div className='text-muted fw-normal'>
                            {colTot[d].signups} הרשמות · {colTot[d].paid} שילמו
                          </div>
                        </td>
                      ))}
                      <td>
                        {ils(costPer(total))}
                        <div className='text-muted fw-normal'>
                          {ils(total.spend)} · {total.signups} הרשמות · {total.paid} שילמו ·{' '}
                          {ils(total.revenue)} הכנסה
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div className='text-muted fs-8'>
              המספר הגדול בכל תא = עלות להרשמה (או ללידים, כשעוד אין הרשמות). "הכי משתלם" מסומן רק
              אחרי מספיק נתונים (3+ הרשמות או 5+ לידים) — לפני כן זה מזל.
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default AbTests
